import { useFetch } from "@hyper-fetch/react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Dumbbell,
  Hand,
  HandHeart,
  HeartPulse,
  Laptop,
  LocateFixed,
  MapPin,
  Medal,
  Salad,
  Search,
  Users,
  Video,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent, ReactNode } from "react";

import { sdk } from "@/api/sdk";
import { nearFrom } from "@/features/directory/utils/search-params";
import type { DirectorySearch } from "@/features/directory/utils/search-params";
import {
  SERVICE_TYPE_COPY,
  SERVICE_TYPE_GROUP_COPY,
  serviceTypeEntries,
  serviceTypeInOtherLanguage,
} from "@/features/directory/utils/service-type-copy";
import { CoachPhoto } from "@/features/site/components/coach-photo";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import {
  CATEGORY_PROFESSION,
  foldSearchText,
  SERVICE_TYPE_GROUP,
  SERVICE_TYPE_GROUP_CATEGORY,
  SERVICE_TYPES,
  searchServiceTypes,
  ServiceType,
  ServiceTypeGroup,
} from "@complete-athlete/enums";
import type { SpecialistProfession } from "@complete-athlete/enums";

/** What most people come for, offered before a word is typed. */
const POPULAR: ServiceType[] = [
  ServiceType.PERSONAL_TRAINING,
  ServiceType.PHYSIO_CONSULTATION,
  ServiceType.NUTRITION_CONSULTATION,
  ServiceType.MANUAL_THERAPY,
  ServiceType.SPORTS_MASSAGE,
  ServiceType.ONLINE_COACHING,
];

export const GROUP_ICON: Record<ServiceTypeGroup, LucideIcon> = {
  [ServiceTypeGroup.ONE_TO_ONE]: Dumbbell,
  [ServiceTypeGroup.ONLINE]: Laptop,
  [ServiceTypeGroup.SPORTS]: Medal,
  [ServiceTypeGroup.CLASSES]: Users,
  [ServiceTypeGroup.NUTRITION]: Salad,
  [ServiceTypeGroup.REHAB]: HeartPulse,
  [ServiceTypeGroup.THERAPIES]: Hand,
  [ServiceTypeGroup.MASSAGE]: HandHeart,
};

/** Typing has to stop this long before people are looked up. Services and cities need no request. */
const PEOPLE_DELAY_MS = 200;
const PEOPLE_SHOWN = 3;
const CITIES_SHOWN = 6;

/** A person the bar found, as the search answers it. */
type DirectoryPerson = ReturnType<typeof usePeople>["people"][number];

type What = { text: string; service?: ServiceType };
type Where = { text: string; near?: string; online?: true };

type Option = {
  id: string;
  icon: LucideIcon;
  /** Someone's face instead of the icon. */
  person?: { name: string; avatarUrl: string | null };
  /** The heading this option starts, in a list with more than one kind of answer. */
  section?: string;
  label: string;
  hint: ReactNode;
  pick: () => void;
};

/** What most people of a profession come for: the first types on its shelves. */
const popularFor = (profession: SpecialistProfession | null | undefined) =>
  profession
    ? SERVICE_TYPES.filter(
        (type) => CATEGORY_PROFESSION[SERVICE_TYPE_GROUP_CATEGORY[SERVICE_TYPE_GROUP[type]]] === profession,
      ).slice(0, POPULAR.length)
    : POPULAR;

/** People whose name, business or place matches what is typed, once the typing pauses. */
const usePeople = (query: string) => {
  const [settled, setSettled] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setSettled(query), PEOPLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query]);
  const asked = settled.length >= 2 && settled === query;
  const answer = useFetch(sdk.directory.search.$get.setQueryParams({ q: settled, sort: "relevance", page: "1" }), {
    disabled: !asked,
    dependencies: [settled],
  });
  return { people: asked ? (answer.data?.cards.slice(0, PEOPLE_SHOWN) ?? []) : [] };
};

const whatFrom = (search: DirectorySearch): What =>
  search.service
    ? {
        text: SERVICE_TYPE_COPY[search.service].name(),
        service: search.service,
      }
    : { text: search.q ?? "" };

const whereFrom = (search: DirectorySearch): Where =>
  search.online
    ? { text: m.directory_search_online(), online: true }
    : search.near
      ? { text: m.directory_search_near_me(), near: search.near }
      : { text: search.city ?? "" };

/**
 * The search everyone starts from: what (a catalogue service, or any words) and where (a city,
 * near me, or online), the way Booksy and ZnanyLekarz ask it. The catalogue is matched here, in
 * the browser, so suggestions appear with the first letter and forgive typos and missing Polish
 * letters. Picking a service moves on to where; picking a place runs the search.
 *
 * The list does not animate: it changes with every keystroke, and motion there only feels slow.
 */
export function SearchBar({
  initial = {},
  className,
  surfaceClassName,
  compact = false,
  cities = [],
  profession,
  onSearch,
  onPickPerson,
  events,
}: {
  initial?: DirectorySearch;
  className?: string;
  /** The bar's own fill and shadow, for a page where it sits among cards rather than floating over a section. */
  surfaceClassName?: string;
  /** On a phone, the button beside the fields rather than under them, for a page where the list matters more. */
  compact?: boolean;
  /** Places with someone listed, offered under where as it is typed. */
  cities?: { city: string; specialists: number }[];
  /** The list the bar sits over: what it suggests before a word is typed. */
  profession?: SpecialistProfession | null;
  /** What a search does on a page that lists the answer itself. Otherwise it opens `/search`. */
  onSearch?: (search: Pick<DirectorySearch, "service" | "q" | "city" | "near" | "online">) => void;
  /** What picking a found person does. Otherwise it opens their business's public page. */
  onPickPerson?: (person: DirectoryPerson) => void;
  /**
   * The bar over the events page: it finds events rather than people, suggests what events there are,
   * and a picked type is the search, since the list is right under it.
   */
  events?: { popular: { service: ServiceType; count: number }[] };
}) {
  const navigate = useNavigate();
  const listId = useId();
  const barRef = useRef<HTMLDivElement>(null);
  const whatRef = useRef<HTMLInputElement>(null);
  const whereRef = useRef<HTMLInputElement>(null);
  const [what, setWhat] = useState<What>(() => whatFrom(initial));
  const [where, setWhere] = useState<Where>(() => whereFrom(initial));
  const [open, setOpen] = useState<"what" | "where" | null>(null);
  const [active, setActive] = useState(0);
  const [locating, setLocating] = useState(false);
  const [locationOff, setLocationOff] = useState(false);
  const entries = useMemo(serviceTypeEntries, []);

  const run = (nextWhat: What, nextWhere: Where) => {
    setOpen(null);
    const service = nextWhat.service ?? serviceTypeInOtherLanguage(nextWhat.text);
    const search = {
      ...(service ? { service } : nextWhat.text ? { q: nextWhat.text } : {}),
      ...(nextWhere.online
        ? { online: true as const }
        : nextWhere.near
          ? { near: nextWhere.near }
          : nextWhere.text
            ? { city: nextWhere.text }
            : {}),
    };
    if (onSearch) onSearch(search);
    else void navigate({ to: "/search", search });
  };

  const pickPerson = (person: DirectoryPerson) => {
    setOpen(null);
    if (onPickPerson) onPickPerson(person);
    else void navigate({ to: "/$trainerSlug", params: { trainerSlug: person.slug } });
  };

  const pickService = (service: ServiceType) => {
    const next = { text: SERVICE_TYPE_COPY[service].name(), service };
    setWhat(next);
    setActive(0);
    // Where comes next, unless it is already answered: then this is the search.
    if (where.text || events) run(next, where);
    else {
      setOpen("where");
      whereRef.current?.focus();
    }
  };

  const pickWhere = (next: Where) => {
    setWhere(next);
    run(what, next);
  };

  const locate = () => {
    setLocationOff(false);
    if (!("geolocation" in navigator)) return setLocationOff(true);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocating(false);
        pickWhere({
          text: m.directory_search_near_me(),
          near: nearFrom(coords),
        });
      },
      () => {
        setLocating(false);
        setLocationOff(true);
        whereRef.current?.focus();
      },
      { maximumAge: 600_000, timeout: 10_000 },
    );
  };

  const serviceOption = (service: ServiceType, term: string | null): Option => {
    const group = SERVICE_TYPE_GROUP[service];
    return {
      id: `service-${service}`,
      icon: GROUP_ICON[group],
      label: SERVICE_TYPE_COPY[service].name(),
      hint: term ? (
        <>
          {SERVICE_TYPE_GROUP_COPY[group]()} · <span className="text-foreground/80">{term}</span>
        </>
      ) : (
        SERVICE_TYPE_GROUP_COPY[group]()
      ),
      pick: () => pickService(service),
    };
  };

  const query = what.service ? "" : what.text.trim();
  const { people } = usePeople(open === "what" && !events ? query : "");
  const eventCount = (count: number) => m.events_search_count({ count });
  const services = query
    ? searchServiceTypes({ query, entries, limit: people.length > 0 ? 4 : 6 }).map((match) =>
        serviceOption(match.type, match.term),
      )
    : [];
  const whatOptions: Option[] = query
    ? [
        ...services.map((option, index) =>
          index === 0 ? { ...option, section: m.directory_search_services() } : option,
        ),
        ...people.map(
          (person, index): Option => ({
            id: `person-${person.organizationId}-${person.specialist.id}`,
            icon: Search,
            person: person.specialist,
            section: index === 0 ? m.directory_search_people() : undefined,
            label: person.specialist.name,
            hint: [person.specialist.title, person.business.name, person.place?.city].filter(Boolean).join(" · "),
            pick: () => pickPerson(person),
          }),
        ),
        {
          id: "anything",
          icon: Search,
          label: m.directory_search_anything({ query }),
          hint: events ? m.events_search_anything_hint() : m.directory_search_anything_hint(),
          pick: () => {
            setWhat({ text: query });
            if (where.text) run({ text: query }, where);
            else {
              setOpen("where");
              whereRef.current?.focus();
            }
          },
        },
      ]
    : events
      ? events.popular.slice(0, POPULAR.length).map(({ service, count }, index) => ({
          ...serviceOption(service, null),
          hint: eventCount(count),
          section: index === 0 ? m.directory_search_popular() : undefined,
        }))
      : popularFor(profession).map((service, index) => ({
          ...serviceOption(service, null),
          section: index === 0 ? m.directory_search_popular() : undefined,
        }));

  const whereOptions: Option[] = [
    {
      id: "near",
      icon: LocateFixed,
      label: locating ? m.directory_search_locating() : m.directory_search_near_me(),
      hint: m.directory_search_near_me_hint(),
      pick: locate,
    },
    {
      id: "online",
      icon: Video,
      label: m.directory_search_online(),
      hint: m.directory_search_online_hint(),
      pick: () => pickWhere({ text: m.directory_search_online(), online: true }),
    },
    ...cities
      .filter(({ city }) => foldSearchText(city).includes(foldSearchText(where.near || where.online ? "" : where.text)))
      .slice(0, CITIES_SHOWN)
      .map(
        ({ city, specialists }, index): Option => ({
          id: `city-${city}`,
          icon: MapPin,
          section: index === 0 ? m.directory_search_cities() : undefined,
          label: city,
          hint: events ? eventCount(specialists) : m.directory_search_city_people({ count: specialists }),
          pick: () => pickWhere({ text: city }),
        }),
      ),
  ];

  const options = open === "what" ? whatOptions : open === "where" ? whereOptions : [];
  const activeId = options[active] ? `${listId}-${options[active].id}` : undefined;

  const onKeyDown = (field: "what" | "where") => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (open !== field) return setOpen(field);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((current) => (current + step + options.length) % options.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      // A typed city is the answer itself; a list item only when the visitor moved onto one.
      if (field === "where" && where.text && !where.near && !where.online && active < 0) return run(what, where);
      if (open === field && options[active] && (field === "what" ? query : active >= 0)) return options[active].pick();
      run(what, where);
    } else if (event.key === "Escape") {
      setOpen(null);
    }
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setOpen(null);
  };

  return (
    <PopoverPrimitive.Root open={Boolean(open && options.length > 0)}>
      <div ref={barRef} className={cn("relative w-full", className)} onBlur={onBlur}>
        <PopoverPrimitive.Anchor asChild>
          <form
            role="search"
            aria-label={events ? m.events_search_label() : m.directory_search_label()}
            onSubmit={(event) => {
              event.preventDefault();
              run(what, where);
            }}
            className={cn(
              "flex flex-col gap-1 rounded-[28px] border border-border bg-background/85 p-1.5 shadow-[0_24px_60px_color-mix(in_oklch,var(--foreground)_16%,transparent)] backdrop-blur-xl md:flex-row md:items-center md:gap-0 md:rounded-full",
              compact &&
                "max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:items-center max-md:gap-x-1.5 max-md:gap-y-0",
              surfaceClassName,
            )}
          >
            <Field
              icon={Search}
              label={m.directory_search_what_label()}
              active={open === "what"}
              className="md:flex-[1.4]"
              clear={
                what.text
                  ? () => {
                      setWhat({ text: "" });
                      setActive(0);
                      whatRef.current?.focus();
                      // On a page that lists the answer, an emptied field is a new answer.
                      if (onSearch) run({ text: "" }, where);
                    }
                  : undefined
              }
            >
              <input
                ref={whatRef}
                role="combobox"
                aria-expanded={open === "what"}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={open === "what" ? activeId : undefined}
                aria-label={m.directory_search_what_label()}
                value={what.text}
                placeholder={events ? m.events_search_what_placeholder() : m.directory_search_what_placeholder()}
                onFocus={() => (setOpen("what"), setActive(what.service ? -1 : 0))}
                onChange={(event) => {
                  setWhat({ text: event.target.value });
                  setOpen("what");
                  setActive(0);
                }}
                onKeyDown={onKeyDown("what")}
                className="w-full min-w-0 truncate bg-transparent text-[15px] font-medium outline-none placeholder:font-normal placeholder:text-muted-foreground"
              />
            </Field>

            <span aria-hidden className="mx-1 hidden h-8 w-px bg-border md:block" />

            <Field
              icon={MapPin}
              label={m.directory_search_where_label()}
              active={open === "where"}
              className="md:flex-1"
              clear={
                where.text
                  ? () => {
                      setWhere({ text: "" });
                      whereRef.current?.focus();
                      if (onSearch) run(what, { text: "" });
                    }
                  : undefined
              }
            >
              <input
                ref={whereRef}
                role="combobox"
                aria-expanded={open === "where"}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={open === "where" ? activeId : undefined}
                aria-label={m.directory_search_where_label()}
                value={where.text}
                placeholder={m.directory_search_where_placeholder()}
                onFocus={() => (setOpen("where"), setActive(-1))}
                onChange={(event) => {
                  setWhere({ text: event.target.value });
                  setLocationOff(false);
                  setActive(-1);
                }}
                onKeyDown={onKeyDown("where")}
                className="w-full min-w-0 truncate bg-transparent text-[15px] font-medium outline-none placeholder:font-normal placeholder:text-muted-foreground"
              />
            </Field>

            <button
              type="submit"
              className={cn(
                "flex h-12 shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-6 text-[15px] font-medium text-primary-foreground transition-[transform,filter] duration-150 ease-out hover:brightness-105 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none active:scale-[0.97] md:h-14 md:px-7",
                compact && "max-md:col-start-2 max-md:row-span-2 max-md:row-start-1 max-md:size-14 max-md:px-0",
              )}
            >
              <Search aria-hidden className="size-[18px]" />
              <span className={compact ? "max-md:sr-only" : undefined}>{m.directory_search_submit()}</span>
            </button>
          </form>
        </PopoverPrimitive.Anchor>

        {locationOff ? (
          <p role="status" className="mt-2 px-5 text-sm text-muted-foreground">
            {m.directory_search_location_off()}
          </p>
        ) : null}

        <PopoverPrimitive.Portal>
          {/* In a portal, so a card that clips its own corners never cuts the list off. */}
          <PopoverPrimitive.Content
            align={open === "where" ? "end" : "start"}
            sideOffset={8}
            collisionPadding={12}
            // Focus stays in the field the visitor is typing in.
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onInteractOutside={(event) => {
              if (event.target instanceof Node && barRef.current?.contains(event.target)) event.preventDefault();
              else setOpen(null);
            }}
            onEscapeKeyDown={() => setOpen(null)}
            className={cn(
              "z-50 w-[var(--radix-popover-trigger-width)] rounded-3xl border border-border bg-popover p-2 text-popover-foreground shadow-[0_24px_60px_color-mix(in_oklch,var(--foreground)_22%,transparent)] outline-none",
              open === "where" ? "md:w-[380px]" : "md:w-[min(520px,var(--radix-popover-trigger-width))]",
            )}
          >
            <ul id={listId} role="listbox" aria-label={m.directory_search_label()} className="flex flex-col">
              {options.map((option, index) => (
                <Fragment key={option.id}>
                  {option.section ? (
                    <li role="presentation" className="px-3 pt-2 pb-1.5 text-xs font-medium text-muted-foreground">
                      {option.section}
                    </li>
                  ) : null}
                  {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- focus stays in the combobox field, whose arrow keys and Enter pick the active option */}
                  <li
                    id={`${listId}-${option.id}`}
                    role="option"
                    aria-selected={index === active}
                    // Keep focus in the field, so typing carries on after a click.
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setActive(index)}
                    onClick={option.pick}
                    className={cn(
                      "flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-2.5",
                      index === active && "bg-muted",
                      option.id === "anything" && "mt-1 border-t border-border pt-3",
                    )}
                  >
                    {option.person ? (
                      <CoachPhoto
                        name={option.person.name}
                        src={option.person.avatarUrl}
                        className="size-9 rounded-xl text-xs"
                      />
                    ) : (
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
                        <option.icon aria-hidden className="size-[18px]" />
                      </span>
                    )}
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[15px] font-medium">{option.label}</span>
                      <span className="truncate text-[13px] text-muted-foreground">{option.hint}</span>
                    </span>
                    {index === active ? (
                      <ArrowUpRight aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                    ) : null}
                  </li>
                </Fragment>
              ))}
            </ul>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </div>
    </PopoverPrimitive.Root>
  );
}

/** One half of the bar: an icon, a small label, the field, and a way to empty it. */
function Field({
  icon: Icon,
  label,
  active,
  clear,
  className,
  children,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  clear?: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label
      className={cn(
        "group/field flex min-w-0 cursor-text items-center gap-3 rounded-full px-4 py-2 transition-colors duration-150 md:px-5",
        active ? "bg-muted" : "hover:bg-muted/60",
        className,
      )}
    >
      <Icon aria-hidden className="size-[18px] shrink-0 text-muted-foreground" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {children}
      </span>
      {clear ? (
        <button
          type="button"
          onClick={clear}
          aria-label={m.directory_search_clear()}
          className="grid size-7 shrink-0 place-items-center rounded-full text-muted-foreground opacity-0 transition-opacity group-focus-within/field:opacity-100 hover:bg-background hover:text-foreground"
        >
          <X aria-hidden className="size-4" />
        </button>
      ) : null}
    </label>
  );
}
